import { Box, Button, CircularProgress, Stack } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePageMetadata } from "../../../../context/PageMetadataContext";
import { usePortal } from "../../../../context/PortalsContext";
import { useToast } from "../../../../context/ToastContext";
import {
  advancedUserSearch,
  createUpdateRule,
  getRuleById,
  getUserFilters,
} from "../../../../services/api/apiClient";
import {
  AdvancedSearchFilter,
  AdvancedSearchSelection,
  BooleanOperator,
  DateOperator,
  ListOperator,
  NumericOperator,
  StringOperator,
} from "../../../../services/api/types/users/users";
import {
  filterImpactEnum,
  UserSearchRow,
} from "../../../../services/api/types/rules/rules";
import { brand } from "../../../../theme/themePrimitives";
import { RuleBuilderFilter } from "./RuleBuilderFilter";
import { ValuesModal } from "../../../UserManagement/Users/components/ValuesModal";
import { RuleDetails } from "./Sections/RuleDetails";
import { TargetAudience } from "./Sections/TargetAudience";
import { LivePreview, PreviewUser } from "./Sections/LivePreview";
import { ConfirmApplyModal } from "./ConfirmApplyModal";
import { extractErrorMessage } from "../../../../utils/errorUtils";

const EMPTY_FILTER: AdvancedSearchFilter = {
  attribute: "",
  operator: "",
  value: "",
  groupHeader: "",
  filterGroup: "",
};

const INITIAL_SELECTIONS: AdvancedSearchSelection[] = [
  { filters: [{ ...EMPTY_FILTER }] },
];

export const AddEditRule: React.FC = () => {
  const { portalUid, assignmentUid, ruleUid } = useParams<{
    portalUid: string;
    assignmentUid: string;
    ruleUid: string;
  }>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { portals } = usePortal();
  const { showToast } = useToast();

  const isAddMode = ruleUid === "new";

  type FilterStorage = {
    ruleUid: string;
    assignmentUid: string;
    selections: AdvancedSearchSelection[];
    chips: string[];
  };

  const readFilterStorage = useCallback((): FilterStorage | null => {
    try {
      const raw = localStorage.getItem("ruleFilters");
      return raw ? (JSON.parse(raw) as FilterStorage) : null;
    } catch {
      return null;
    }
  }, []);

  const persistFilter = useCallback(
    (sels: AdvancedSearchSelection[], chips: string[]) => {
      localStorage.setItem(
        "ruleFilters",
        JSON.stringify({ ruleUid, assignmentUid, selections: sels, chips }),
      );
    },
    [ruleUid, assignmentUid],
  );

  const [previewSearch, setPreviewSearch] = useState("");
  const [previewPage, setPreviewPage] = useState(1);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

  const ruleDetailsRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState("");
  const [portalGroupUid, setPortalGroupUid] = useState<
    string | null | undefined
  >(undefined);
  const [newStartersDays, setNewStartersDays] = useState("");
  const [existingStaffDays, setExistingStaffDays] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [ruleDetailsErrors, setRuleDetailsErrors] = useState<{
    name?: string;
    portalGroupUid?: string;
    newStartersDays?: string;
    existingStaffDays?: string;
  }>({});
  const [previewData, setPreviewData] = useState<PreviewUser[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Filter sidebar state — use localStorage only if it belongs to this exact rule
  const [showFilter, setShowFilter] = useState(false);
  const [selections, setSelections] = useState<AdvancedSearchSelection[]>(
    () => {
      const stored = readFilterStorage();
      return stored !== null &&
        stored.ruleUid === ruleUid &&
        stored.assignmentUid === assignmentUid
        ? stored.selections
        : INITIAL_SELECTIONS;
    },
  );
  const [appliedChips, setAppliedChips] = useState<string[]>(() => {
    const stored = readFilterStorage();
    return stored !== null &&
      stored.ruleUid === ruleUid &&
      stored.assignmentUid === assignmentUid
      ? stored.chips
      : [];
  });

  const portalId = portals.selectedPortal?.portalUid ?? portalUid ?? "";

  // The filter draft only exists to survive an accidental page reload while
  // editing — any navigation away from this page (breadcrumb, browser back,
  // Cancel, successful Save, ...) should discard it, not just Cancel/Save.
  useEffect(() => {
    return () => {
      localStorage.removeItem("ruleFilters");
    };
  }, []);

  useEffect(() => {
    if (isAddMode || !portalId || !assignmentUid || !ruleUid) return;
    getRuleById(portalId, assignmentUid, ruleUid)
      .then((rule) => {
        // Always repopulate form fields from the API response
        setName(rule.name);
        setPortalGroupUid(rule.portalGroupUid ?? null);
        setNewStartersDays(
          rule.deadlineNewStarter != null
            ? String(rule.deadlineNewStarter)
            : "",
        );
        setExistingStaffDays(
          rule.deadlineExistingUser != null
            ? String(rule.deadlineExistingUser)
            : "",
        );

        // Only load filters from the API when there is no localStorage draft.
        // If a draft exists the user already has unsaved changes — keep them.
        const stored = readFilterStorage();
        const hasDraftForThisRule =
          stored !== null &&
          stored.ruleUid === ruleUid &&
          stored.assignmentUid === assignmentUid;
        if (!hasDraftForThisRule) {
          const loadedSelections = rule.filters?.selections?.length
            ? rule.filters.selections
            : INITIAL_SELECTIONS;
          const loadedChips = rule.filtersText
            ? rule.filtersText
                .split(/ OR /i)
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
          setSelections(loadedSelections);
          setAppliedChips(loadedChips);
          // Seed localStorage so future refreshes preserve the loaded state
          persistFilter(loadedSelections, loadedChips);
        }

        if (rule.finResult?.length) {
          setPreviewData(
            rule.finResult.map((u) => ({
              id: u.UserPortalUid,
              firstName: u.firstName,
              lastName: u.lastName,
              email: u.email,
              impact:
                u.filterImpact === filterImpactEnum.ADDED
                  ? filterImpactEnum.ADDED
                  : u.filterImpact === filterImpactEnum.REMOVED
                    ? filterImpactEnum.REMOVED
                    : u.filterImpact === filterImpactEnum.ASSIGNED_BY_OTHER_RULE
                      ? filterImpactEnum.ASSIGNED_BY_OTHER_RULE
                      : filterImpactEnum.UNCHANGED,
            })),
          );
        }
      })
      .catch(() => {});
  }, [
    isAddMode,
    portalId,
    assignmentUid,
    ruleUid,
    readFilterStorage,
    persistFilter,
  ]);

  // Values modal state
  const [openValuesModal, setOpenValuesModal] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [valuesLoading, setValuesLoading] = useState(false);
  const [currentSelectionIndex, setCurrentSelectionIndex] = useState(0);
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const [selectedOperator, setSelectedOperator] = useState<
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | ""
  >("");

  const rulesPath = `/portal-configuration/${portalUid}/assignment-search/${assignmentUid}/rules`;
  // Carry the portal-scoped active flag from the assignment list back to the
  // Rules screen; without it Rules falls back to the single-assignment
  // endpoint's (owning portal's) status and can wrongly redirect away.
  const rulesState = {
    assignmentTitle: state?.assignmentTitle,
    assignmentActiveOnThisPortal: state?.assignmentActiveOnThisPortal,
  };

  usePageMetadata(
    {
      title: isAddMode ? "Add new rule" : "Edit rule",
      titleBadge: state?.assignmentTitle || undefined,
      breadcrumbs: [
        {
          label: "Assignment management",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Assignment search",
          path: `/portal-configuration/${portalUid}/assignment-search`,
        },
        {
          label: "Rules",
          path: rulesPath,
          state: rulesState,
        },
        {
          label: isAddMode ? "Add new rule" : "Edit rule",
        },
      ],
    },
    [
      portalUid,
      assignmentUid,
      isAddMode,
      state?.ruleName,
      state?.assignmentTitle,
      state?.assignmentActiveOnThisPortal,
    ],
  );

  const handleChange = <K extends keyof AdvancedSearchFilter>(
    selectionIndex: number,
    filterIndex: number,
    field: K,
    value: AdvancedSearchFilter[K],
    groupHeader?: string,
    filterGroup?: string,
    initialValue?: string,
  ) => {
    const newSelections = [...selections];
    const newFilters = [...newSelections[selectionIndex].filters];

    if (field === "attribute") {
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        attribute: value as string,
        operator: "",
        value: initialValue ?? "",
        groupHeader: groupHeader ?? newFilters[filterIndex].groupHeader ?? "",
        filterGroup: filterGroup ?? newFilters[filterIndex].filterGroup ?? "",
      };
    } else if (field === "operator") {
      newFilters[filterIndex] = {
        ...newFilters[filterIndex],
        operator: value as AdvancedSearchFilter["operator"],
      };
    } else {
      newFilters[filterIndex] = { ...newFilters[filterIndex], [field]: value };
    }

    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: newFilters,
    };
    setSelections(newSelections);
    persistFilter(newSelections, appliedChips);
  };

  const addRow = (selectionIndex: number) => {
    const newSelections = [...selections];
    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: [...newSelections[selectionIndex].filters, { ...EMPTY_FILTER }],
    };
    setSelections(newSelections);
    persistFilter(newSelections, appliedChips);
  };

  const removeRow = (selectionIndex: number, filterIndex: number) => {
    if (selections[selectionIndex].filters.length <= 1) return;
    const newSelections = [...selections];
    newSelections[selectionIndex] = {
      ...newSelections[selectionIndex],
      filters: newSelections[selectionIndex].filters.filter(
        (_, i) => i !== filterIndex,
      ),
    };
    setSelections(newSelections);
    persistFilter(newSelections, appliedChips);
  };

  const addSelection = () => {
    const next = [...selections, { filters: [{ ...EMPTY_FILTER }] }];
    setSelections(next);
    persistFilter(next, appliedChips);
  };

  const removeSelection = (selectionIndex: number) => {
    if (selections.length === 1) return;
    const next = selections.filter((_, i) => i !== selectionIndex);
    setSelections(next);
    persistFilter(next, appliedChips);
  };

  const clearAll = () => {
    setSelections(INITIAL_SELECTIONS);
    setAppliedChips([]);
    localStorage.removeItem("ruleFilters");
  };

  const handleSearch = async (chips: string[]) => {
    setAppliedChips(chips);
    persistFilter(selections, chips);
    setShowFilter(false);
    setPreviewLoading(true);
    try {
      const response: { finResult: UserSearchRow[] } = await advancedUserSearch(
        portalId,
        {
          selections,
          ruleId: ruleUid,
          assignmentId: assignmentUid,
        },
      );
      setPreviewData(
        (response.finResult ?? []).map((u) => ({
          id: u.UserPortalUid,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          impact:
            u.filterImpact === filterImpactEnum.ADDED
              ? filterImpactEnum.ADDED
              : u.filterImpact === filterImpactEnum.REMOVED
                ? filterImpactEnum.REMOVED
                : u.filterImpact === filterImpactEnum.ASSIGNED_BY_OTHER_RULE
                  ? filterImpactEnum.ASSIGNED_BY_OTHER_RULE
                  : filterImpactEnum.UNCHANGED,
        })),
      );
    } catch {
      showToast("Failed to load preview. Please try again.", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const getValuesForAttribute = async (
    attribute: string,
    operator:
      | StringOperator
      | NumericOperator
      | DateOperator
      | BooleanOperator
      | ListOperator
      | "",
    value: string,
    selectionIndex: number,
    filterIndex: number,
  ) => {
    if (!selections[selectionIndex]?.filters[filterIndex]?.attribute.trim())
      return;
    try {
      if (portals.selectedPortal) {
        setCurrentFilterIndex(filterIndex);
        setCurrentSelectionIndex(selectionIndex);
        setSelectedOperator(operator);
        setOpenValuesModal(true);
        setValuesLoading(true);
        const response: any[] = await getUserFilters(
          portals.selectedPortal.portalUid,
          { userAttributeKey: attribute, userAttributeValue: value, operator },
        );
        setSelectedValues(
          response
            .map((item) => item[attribute])
            .filter((v): v is string => typeof v === "string"),
        );
      }
    } catch (error: any) {
      showToast(error?.response?.data ?? "Failed to fetch values", "error");
    } finally {
      setValuesLoading(false);
    }
  };

  const addSelectedValuesToFilter = (values: string[]) => {
    const current =
      selections[currentSelectionIndex].filters[currentFilterIndex].value;
    const existing = current
      ? current
          .split(/[|,]/)
          .map((v) => v.trim())
          .filter(Boolean)
      : [];
    const merged = Array.from(new Set([...existing, ...values]));
    handleChange(
      currentSelectionIndex,
      currentFilterIndex,
      "value",
      merged.join("|"),
    );
    setOpenValuesModal(false);
  };

  const hasFilters = appliedChips.length > 0;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const addedCount = previewData.filter((r) => r.impact === "Added").length;
  const removedCount = previewData.filter((r) => r.impact === "Removed").length;

  const filteredPreviewData = previewSearch
    ? previewData.filter(
        (u) =>
          `${u.firstName} ${u.lastName}`
            .toLowerCase()
            .includes(previewSearch.toLowerCase()) ||
          u.email.toLowerCase().includes(previewSearch.toLowerCase()),
      )
    : previewData;

  const buildBody = () => ({
    ...(isAddMode ? {} : { ruleUid }),
    name,
    portalGroupUid: portalGroupUid ?? null,
    deadlineNewStarter: newStartersDays !== "" ? Number(newStartersDays) : null,
    deadlineExistingUser:
      existingStaffDays !== "" ? Number(existingStaffDays) : null,
    filters: { selections },
  });

  const saveRule = async () => {
    setSaveLoading(true);
    try {
      await createUpdateRule(portalId, assignmentUid!, buildBody());
      localStorage.removeItem("ruleFilters");
      navigate(rulesPath, {
        state: rulesState,
      });
    } catch (error: any) {
      showToast(
        extractErrorMessage(error, "Failed to save rule. Please try again."),
        "error",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const validate = (): typeof ruleDetailsErrors => {
    const errs: typeof ruleDetailsErrors = {};
    if (!name.trim()) errs.name = "Rule name is required";
    if (portalGroupUid === undefined)
      errs.portalGroupUid = "Placement is required";
    if (!newStartersDays)
      errs.newStartersDays = "New starters deadline is required";
    if (!existingStaffDays)
      errs.existingStaffDays = "Existing staff deadline is required";
    return errs;
  };

  const handleCancel = () => {
    localStorage.removeItem("ruleFilters");
    navigate(rulesPath, {
      state: rulesState,
    });
  };

  const handleSave = () => {
    const errs = validate();

    if (Object.keys(errs).length > 0) {
      setRuleDetailsErrors(errs);
      ruleDetailsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    setRuleDetailsErrors({});
    if (isAddMode) {
      saveRule();
    } else {
      setConfirmOpen(true);
    }
  };

  return (
    <Stack spacing={3}>
      <Box ref={ruleDetailsRef}>
        <RuleDetails
          name={name}
          onNameChange={(v) => {
            setName(v);
            setRuleDetailsErrors((e) => ({ ...e, name: undefined }));
          }}
          portalId={portalId}
          portalGroupUid={portalGroupUid}
          onPortalGroupUidChange={(v) => {
            setPortalGroupUid(v);
            setRuleDetailsErrors((e) => ({ ...e, portalGroupUid: undefined }));
          }}
          newStartersDays={newStartersDays}
          onNewStartersDaysChange={(v) => {
            setNewStartersDays(v);
            setRuleDetailsErrors((e) => ({ ...e, newStartersDays: undefined }));
          }}
          existingStaffDays={existingStaffDays}
          onExistingStaffDaysChange={(v) => {
            setExistingStaffDays(v);
            setRuleDetailsErrors((e) => ({
              ...e,
              existingStaffDays: undefined,
            }));
          }}
          errors={ruleDetailsErrors}
        />
      </Box>
      <TargetAudience
        hasFilters={hasFilters}
        appliedChips={appliedChips}
        setShowFilter={setShowFilter}
      />
      <LivePreview
        hasFilters={hasFilters}
        data={filteredPreviewData.slice(
          (previewPage - 1) * previewRowsPerPage,
          previewPage * previewRowsPerPage,
        )}
        totalCount={filteredPreviewData.length}
        addedCount={addedCount}
        removedCount={removedCount}
        page={previewPage}
        onPageChange={setPreviewPage}
        rowsPerPage={previewRowsPerPage}
        onRowsPerPageChange={(r) => {
          setPreviewRowsPerPage(r);
          setPreviewPage(1);
        }}
        search={previewSearch}
        onSearchChange={(v) => {
          setPreviewSearch(v);
          setPreviewPage(1);
        }}
        isLoading={previewLoading}
      />

      <Box display="flex" justifyContent="center" gap={1.5} pb={2}>
        {!isAddMode && (
          <Button
            onClick={handleCancel}
            disabled={saveLoading}
            sx={{
              border: "1px solid #D0D5DD",
              borderRadius: "12px",
              p: "10px 16px",
              backgroundColor: "white !important",
              color: "black",
              "&:hover": { backgroundColor: "whitesmoke !important" },
            }}
          >
            Cancel
          </Button>
        )}
        <Button
          sx={{
            backgroundColor: `${brand[600]} !important`,
            color: "white",
            borderRadius: "12px",
            p: "10px 16px",
            "&:hover": { bgcolor: `${brand[700]} !important` },
            "&.Mui-disabled": {
              color: "white",
              backgroundColor: `${brand[600]} !important`,
            },
          }}
          onClick={handleSave}
          disabled={saveLoading}
        >
          {saveLoading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress
                size={16}
                thickness={5}
                sx={{ color: "white" }}
              />
              <span color="white">
                {isAddMode ? "Save rule" : "Update rule"}
              </span>
            </Stack>
          ) : isAddMode ? (
            "Save rule"
          ) : (
            "Update rule"
          )}
        </Button>
      </Box>

      <RuleBuilderFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        selections={selections}
        handleChange={handleChange}
        addRow={addRow}
        removeRow={removeRow}
        addSelection={addSelection}
        removeSelection={removeSelection}
        clearAll={clearAll}
        handleSearch={handleSearch}
        getValuesForAttribute={getValuesForAttribute}
        loading={false}
      />

      <ConfirmApplyModal
        open={confirmOpen}
        addedCount={addedCount}
        removedCount={removedCount}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={saveRule}
        loading={saveLoading}
      />

      <ValuesModal
        openModal={openValuesModal}
        setOpenModal={setOpenValuesModal}
        values={selectedValues}
        buttonLoading={false}
        valuesLoading={valuesLoading}
        onAddSelectedValues={addSelectedValuesToFilter}
        operator={selectedOperator}
        preSelectedValues={
          selections[currentSelectionIndex]?.filters[currentFilterIndex]?.value
        }
      />
    </Stack>
  );
};
